import React from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import { FormattedMessage, defineMessages } from 'react-intl';

import Container from '../Container';
import Icon from '../Icon';
import Button from '../Button';
import Input from '../Input';
import Loader from '../Loader';
import Dimmer from '../Dimmer';
import Segment from '../Segment';
import Form from '../Form';
import Label from '../Label';
import Message from '../Message';
import Header from '../Header';
import PageTitle from '../PageTitle';
import './styles.css';
import * as constants from '../../containers/Authorization/constants';

const messages = defineMessages({
  addUser: {
    id: 'user.add.caption',
    defaultMessage: 'Add New User',
  },
  firstName: {
    id: 'user.firstName.label',
    defaultMessage: 'First Name',
  },
  lastName: {
    id: 'user.lastName.label',
    defaultMessage: 'Last Name',
  },
  email: {
    id: 'user.email.label',
    defaultMessage: 'Email',
  },
  role: {
    id: 'user.role.label',
    defaultMessage: 'Role',
  },
  buttonSave: {
    id: 'common.button.save',
    defaultMessage: 'SAVE',
  },
  buttonCancel: {
    id: 'common.button.cancel',
    defaultMessage: 'CANCEL',
  },
  generalError: {
    id: 'user.create.error.general',
    defaultMessage: 'Unable to create user',
  },
  userExists: {
    id: 'user.create.error.userExists',
    defaultMessage: 'User with provided email already exists!',
  },
  administrator: {
    id: 'user.update.role.administrator',
    defaultMessage: 'Administrator (All access)',
  },
  recruiter: {
    id: 'user.update.role.recruiter',
    defaultMessage: 'Recruiter',
  },
  coordinator: {
    id: 'user.update.role.coordinator',
    defaultMessage: 'Coordinator',
  },
  director: {
    id: 'user.update.role.director',
    defaultMessage: 'Director',
  },
  publishing: {
    id: 'user.update.role.publishing',
    defaultMessage: 'Publishing (Community access + College Profile Management)',
  },
  community: {
    id: 'user.update.role.community',
    defaultMessage: 'Community (Community access only)',
  },
  reports: {
    id: 'user.update.role.reports',
    defaultMessage: 'Reports',
  },
});

const repvisitMessages = defineMessages({
  administrator: {
    id: 'user.update.role.administrator',
    defaultMessage: 'Administrator (All access)',
  },
  publishing: {
    id: 'repvisits.user.update.role.publishing',
    defaultMessage: 'Publishing (Community access + RepVisits + College Profile Management)',
  },
  community: {
    id: 'repvisits.user.update.role.community',
    defaultMessage: 'Community (Community + RepVisits access)',
  },
});

export const elementIds = {
  firstNameId: 'user-form-first-name',
  lastNameId: 'user-form-last-name',
  emailId: 'user-form-email',
  roleId: 'user-form-role',
};
const missingValueLabel = (
  <Label role="tooltip" basic color="red" pointing>
    <FormattedMessage id="common.label.missingValue" defaultMessage="Please enter a value" />
  </Label>
);

function validateForm(formData) {
  return {
    missingFirstName: !formData.firstName,
    missingLastName: !formData.lastName,
    missingEmail: !formData.email,
    missingRole: !formData.role,
  };
}

export default class CreateUserComponent extends React.PureComponent {
  static propTypes = {
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    requesting: PropTypes.bool,
    createError: PropTypes.shape({
      generalError: PropTypes.bool,
      userExists: PropTypes.bool,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errors: {},
      role: undefined,
    };
  }

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    e.preventDefault();
    const formData = omit(this.state, 'errors');
    const errors = validateForm(formData);
    this.setState({ errors });
    if (!Object.keys(errors).some((key) => errors[key])) {
      this.props.onSave(formData);
    }
  };

  render() {
    const { requesting, createError, onCancel } = this.props;
    const { errors, firstName, lastName, email, role } = this.state;
    const { firstNameId, lastNameId, emailId, roleId } = elementIds;
    const missingFirstNameLabel = errors.missingFirstName ? missingValueLabel : '';
    const missingLastNameLabel = errors.missingLastName ? missingValueLabel : '';
    const missingEmailLabel = errors.missingEmail ? missingValueLabel : '';
    const missingRoleLabel = errors.missingRole ? missingValueLabel : '';

    const errorDetails = createError && Object.keys(createError).length === 1 ? (
      <Message role="alert" negative>
        <Message.Header>
          <FormattedMessage data-cy="add-new-user-negative-message" {...messages[Object.keys(createError)[0]]} />
        </Message.Header>
      </Message>
    ) : '';

    return (
      // Check heAccount for correct info that backend might use
      <Container text>
        <PageTitle title="User" subTitle="Add New User" category="Intersect" />
        <Header as="h1">
          <FormattedMessage {...messages.addUser} />
        </Header>
        <Dimmer.Dimmable as={Form} id="form-create-user" onSubmit={this._onSubmit} size="large">
          <Dimmer
            role="progressbar"
            aria-describedby="Creating User..."
            aria-busy={requesting}
            inverted
            active={requesting}
          >
            <Loader active={requesting} />
          </Dimmer>
          <Segment>
            <Form.Field>
              <label htmlFor={firstNameId}>
                <FormattedMessage {...messages.firstName} />
              </label>
              <Input type="text" name="firstName" onChange={this._onChange} value={firstName || ''}>
                <input id={firstNameId} aria-required required aria-invalid={!!missingFirstNameLabel} />
              </Input>
              {missingFirstNameLabel}
            </Form.Field>
            <Form.Field>
              <label htmlFor={lastNameId}>
                <FormattedMessage {...messages.lastName} />
              </label>
              <Input type="text" name="lastName" onChange={this._onChange} value={lastName || ''}>
                <input id={lastNameId} aria-required required aria-invalid={!!missingLastNameLabel} />
              </Input>
              {missingLastNameLabel}
            </Form.Field>
            <Form.Field>
              <label htmlFor={emailId}>
                <FormattedMessage {...messages.email} />
              </label>
              <Input type="email" name="email" onChange={this._onChange} value={email || ''}>
                <input
                  id={emailId}
                  aria-required
                  pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$"
                  required
                  aria-invalid={!!missingEmailLabel}
                />
              </Input>
              {missingEmailLabel}
            </Form.Field>
            <Form.Field>
              <fieldset aria-label={roleId} id={roleId} styleName={roleId} role="radiogroup" aria-required>
                <legend styleName="role-legend">
                  <FormattedMessage {...messages.role} />
                </legend>
                {constants.APP_ROLES.map((roleConst) => (
                  <div className="ui radio checkbox" key={`role_${roleConst}`} styleName="checkbox">
                    <input
                      id={`role_${roleConst}`}
                      type="radio"
                      name="role"
                      aria-label={roleConst}
                      value={roleConst}
                      checked={role === roleConst}
                      onChange={this._onChange}
                      styleName="role-checkbox"
                      required
                    />
                    <label htmlFor={`role_${roleConst}`} styleName="role-label">
                      <FormattedMessage {...repvisitMessages[roleConst]} />
                    </label>
                  </div>
                ))}
              </fieldset>
              {missingRoleLabel}
            </Form.Field>
            <Button type="button" onClick={onCancel}>
              <Icon name="remove" />
              <FormattedMessage {...messages.buttonCancel} />
            </Button>
            <Button primary type="submit">
              <Icon name="checkmark" />
              <FormattedMessage {...messages.buttonSave} />
            </Button>
          </Segment>
        </Dimmer.Dimmable>
        {errorDetails}
      </Container>
    );
  }
}
